import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileDown, Loader2, LogOut, Paperclip, Pencil, Plus, Trash2, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const fmt = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v ?? 0);

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

// Bouton de suppression à double clic (1er clic arme, 2e confirme)
function ConfirmDelete({ onConfirm, testid, small }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => (armed ? onConfirm() : setArmed(true))}
      className={`inline-flex items-center gap-2 rounded-full font-mono uppercase tracking-[0.12em] transition-colors ${
        small ? "px-3 py-1.5 text-[10px]" : "px-5 py-2.5 text-[11px]"
      } ${armed ? "bg-red-500/20 text-red-300 border border-red-400/40" : "text-[#A0B2CE] border border-white/10 hover:text-red-300 hover:border-red-400/30"}`}
    >
      <Trash2 className="w-3.5 h-3.5" /> {armed ? "Confirmer ?" : "Supprimer"}
    </button>
  );
}

// ---------- Connexion (identique à l'espace client) ----------
function JsmLogin() {
  const { loginClient, user, logout } = useAuth();
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role !== "client") {
    return (
      <div className="max-w-md mx-auto text-center space-y-6" data-testid="jsm-admin-notice">
        <p className="text-[#A0B2CE]">« Je suis mon budget » est réservé aux structures clientes. Vous êtes connectée en administration.</p>
        <button onClick={logout} data-testid="jsm-switch-account"
          className="inline-flex items-center gap-2 bg-gold text-ink px-6 py-3 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors">
          <LogOut className="w-4 h-4" /> Changer de compte
        </button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await loginClient(code, email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Connexion impossible.");
    } finally { setLoading(false); }
  };

  return (
    <motion.div {...fadeUp} className="w-full max-w-md mx-auto" data-testid="jsm-login">
      <p className="text-[#A0B2CE] mb-8">
        Identifiez-vous avec l'email de votre structure et votre mot de passe — les mêmes que pour votre espace client.
      </p>
      <form onSubmit={submit} className="space-y-6" data-testid="jsm-login-form">
        <div>
          <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">
            Code à 4 caractères <span className="text-[#5A6B8C]">— optionnel</span>
          </label>
          <input type="text" maxLength={4} value={code} onChange={(e) => setCode(e.target.value)} data-testid="jsm-login-code"
            className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg tracking-[0.4em] uppercase transition-colors duration-300" />
        </div>
        <div>
          <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="jsm-login-email"
            className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300" />
        </div>
        <div>
          <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Mot de passe</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="jsm-login-password"
            className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300" />
        </div>
        {error && <p className="text-red-400 text-sm font-mono" data-testid="jsm-login-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="jsm-login-submit"
          className="w-full inline-flex items-center justify-center gap-3 bg-gold text-ink py-4 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors duration-300 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
        </button>
        <p className="text-center">
          <Link to="/connexion" className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#A0B2CE] hover:text-gold transition-colors">
            Mot de passe oublié ?
          </Link>
        </p>
      </form>
    </motion.div>
  );
}

// ---------- Accueil : liste des analytiques ----------
function JsmHome({ onOpen }) {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [tva, setTva] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/jsm/analytiques").then((r) => setItems(r.data.analytiques)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/jsm/analytiques", { title: title.trim(), tva });
      toast.success(`Analytique « ${data.analytique.title} » créé`);
      setCreating(false); setTitle(""); setTva(false);
      onOpen(data.analytique.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <motion.div {...fadeUp} data-testid="jsm-home">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-4">Votre spectacle, action ou projet</p>

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
      ) : (
        <>
          {items.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="jsm-list">
              {items.map((a) => (
                <button key={a.id} onClick={() => onOpen(a.id)} data-testid={`jsm-card-${a.id}`}
                  className="text-left border border-white/10 rounded-2xl p-6 hover:border-gold/50 hover:bg-gold/[0.03] transition-colors duration-300">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif text-xl leading-snug">{a.title}</h3>
                    {a.tva && <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-gold border border-gold/30 rounded-full px-2.5 py-1 shrink-0">TVA</span>}
                  </div>
                  <p className="font-mono text-[11px] text-[#A0B2CE] mt-3 uppercase tracking-[0.12em]">
                    {a.nb_lignes} ligne{a.nb_lignes > 1 ? "s" : ""} · Solde{" "}
                    <span className={a.totaux.solde >= 0 ? "text-emerald-400" : "text-red-400"}>{fmt(a.totaux.solde)}</span>
                  </p>
                </button>
              ))}
            </div>
          )}
          {items.length === 0 && (
            <p className="text-[#A0B2CE] mb-10" data-testid="jsm-empty">Aucun analytique pour l'instant — créez le premier.</p>
          )}

          {!creating ? (
            <button onClick={() => setCreating(true)} data-testid="jsm-create-open"
              className="inline-flex items-center gap-3 bg-gold text-ink px-7 py-4 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors duration-300">
              <Plus className="w-4 h-4" /> Créer un nouvel analytique
            </button>
          ) : (
            <form onSubmit={create} className="border border-gold/25 rounded-2xl bg-gold/[0.04] p-6 max-w-lg space-y-6" data-testid="jsm-create-form">
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Titre</label>
                <input type="text" required autoFocus value={title} onChange={(e) => setTitle(e.target.value)} data-testid="jsm-create-title"
                  placeholder="Ex. Tournée printemps 2027"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300 placeholder:text-[#5A6B8C]" />
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE] block mb-3">TVA</label>
                <div className="flex gap-2">
                  {[{ v: false, l: "Non" }, { v: true, l: "Oui" }].map((o) => (
                    <button type="button" key={o.l} onClick={() => setTva(o.v)} data-testid={`jsm-create-tva-${o.l.toLowerCase()}`}
                      className={`px-6 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] transition-colors ${
                        tva === o.v ? "bg-gold text-ink" : "border border-white/15 text-[#A0B2CE] hover:text-gold hover:border-gold/40"}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button type="submit" disabled={busy || !title.trim()} data-testid="jsm-create-submit"
                  className="inline-flex items-center gap-3 bg-gold text-ink px-7 py-3.5 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors duration-300 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}
                </button>
                <button type="button" onClick={() => setCreating(false)} data-testid="jsm-create-cancel"
                  className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#A0B2CE] hover:text-gold transition-colors">
                  Annuler
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </motion.div>
  );
}

// ---------- Formulaire d'ajout d'une ligne ----------
function LigneForm({ kind, analytique, categories, onAdded, onCancel, initial }) {
  const isDep = kind === "depense";
  const editing = Boolean(initial);
  const list = isDep ? categories.depenses : categories.recettes;
  const [label, setLabel] = useState(initial?.label || "");
  const [tiers, setTiers] = useState(initial?.tiers || "");
  const [ht, setHt] = useState(initial?.ht ?? "");
  const [tva, setTva] = useState(initial?.tva ?? "");
  const [ttc, setTtc] = useState(initial?.ttc ?? "");
  const [net, setNet] = useState(initial?.net ?? "");
  const [date, setDate] = useState(initial?.date || new Date().toLocaleDateString("sv-SE"));
  const [statut, setStatut] = useState(initial?.statut || "a_payer");
  const [justif, setJustif] = useState(null);
  const [busy, setBusy] = useState(false);
  const withTva = analytique.tva;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const num = (s) => (s === "" ? null : parseFloat(String(s).replace(",", ".")));
    try {
      const payload = { kind, label, tiers: tiers.trim(), date, statut };
      if (withTva) Object.assign(payload, { ht: num(ht), tva: num(tva), ttc: num(ttc) });
      else payload.net = num(net);
      const { data } = editing
        ? await api.patch(`/jsm/lignes/${initial.id}`, payload)
        : await api.post(`/jsm/analytiques/${analytique.id}/lignes`, payload);
      if (justif) {
        const fd = new FormData();
        fd.append("file", justif);
        await api.post(`/jsm/lignes/${data.ligne.id}/justificatif`, fd);
      }
      toast.success(editing ? "Ligne modifiée" : "Ligne ajoutée");
      onAdded(data.ligne);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const amountField = (value, setter, placeholder, testid) => (
    <input type="number" step="0.01" min="0" required value={value} onChange={(e) => setter(e.target.value)}
      placeholder={placeholder} data-testid={testid}
      className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300 placeholder:text-[#5A6B8C]" />
  );

  return (
    <form onSubmit={submit} className="border border-gold/25 rounded-2xl bg-gold/[0.04] p-6 space-y-6" data-testid={`jsm-form-${kind}`}>
      <div>
        <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">
          {isDep ? "Poste de dépense" : "Poste de recette"}
        </label>
        <select required value={label} onChange={(e) => setLabel(e.target.value)} data-testid="jsm-ligne-categorie"
          className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300 [&>option]:bg-ink">
          <option value="" disabled>Choisir…</option>
          {list.map((c, i) => <option key={`${c.code}-${i}`} value={c.label}>{c.label} {c.code}</option>)}
        </select>
      </div>
      <div>
        <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">
          {isDep ? "Fournisseur" : "Client"}
        </label>
        <input type="text" required value={tiers} onChange={(e) => setTiers(e.target.value)} data-testid="jsm-ligne-tiers"
          placeholder={isDep ? "Nom du fournisseur" : "Nom du client"}
          className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300 placeholder:text-[#5A6B8C]" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Date</label>
          <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} data-testid="jsm-ligne-date"
            className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300 [color-scheme:dark]" />
        </div>
        <div>
          <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE] block mb-3">État</label>
          <div className="flex gap-2">
            {[["a_payer", "À payer"], ["paye", "Payé"]].map(([v, l]) => (
              <button type="button" key={v} onClick={() => setStatut(v)} data-testid={`jsm-ligne-statut-${v}`}
                className={`px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] transition-colors ${
                  statut === v
                    ? v === "paye" ? "bg-emerald-400/20 text-emerald-200 border border-emerald-400/50" : "bg-amber-400/20 text-amber-200 border border-amber-400/50"
                    : "border border-white/15 text-[#A0B2CE] hover:text-gold hover:border-gold/40"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div>
        <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">
          Pièce justificative <span className="text-[#5A6B8C]">— optionnelle (PDF, image, 10 Mo max)</span>
        </label>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={(e) => setJustif(e.target.files[0] || null)}
          data-testid="jsm-ligne-justificatif"
          className="w-full py-3 text-sm text-[#A0B2CE] file:mr-4 file:rounded-full file:border file:border-white/15 file:bg-transparent file:px-4 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.12em] file:text-[#F3F4F6] hover:file:border-gold/50 file:cursor-pointer" />
      </div>
      {withTva ? (
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">HT</label>
            {amountField(ht, setHt, "0,00", "jsm-ligne-ht")}
          </div>
          <div>
            <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">TVA</label>
            {amountField(tva, setTva, "0,00", "jsm-ligne-tva")}
          </div>
          <div>
            <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">TTC</label>
            {amountField(ttc, setTtc, "0,00", "jsm-ligne-ttc")}
          </div>
        </div>
      ) : (
        <div>
          <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Montant NET</label>
          {amountField(net, setNet, "0,00", "jsm-ligne-net")}
        </div>
      )}
      <div className="flex items-center gap-4">
        <button type="submit" disabled={busy || !label || !tiers.trim()} data-testid="jsm-ligne-submit"
          className="inline-flex items-center gap-3 bg-gold text-ink px-7 py-3.5 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors duration-300 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Modifier" : "Ajouter"}
        </button>
        <button type="button" onClick={onCancel} data-testid="jsm-ligne-cancel"
          className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#A0B2CE] hover:text-gold transition-colors">
          Annuler
        </button>
      </div>
    </form>
  );
}

// ---------- Un analytique ouvert ----------
function JsmAnalytique({ aid, onBack }) {
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState(null);
  const [formKind, setFormKind] = useState(null); // null | "depense" | "recette"
  const [editing, setEditing] = useState(null); // ligne en cours de modification
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = () => api.get(`/jsm/analytiques/${aid}`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); api.get("/jsm/categories").then((r) => setCategories(r.data)); }, [aid]);

  if (data === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;
  if (data === false) return <p className="text-[#A0B2CE]" data-testid="jsm-not-found">Analytique introuvable.</p>;

  const { analytique: a, lignes, totaux: t } = data;

  const addLigne = (ligne) => {
    setData((prev) => ({ ...prev, lignes: [...prev.lignes, ligne] }));
    setFormKind(null);
    load();
  };
  const removeLigne = async (lid) => {
    try {
      await api.delete(`/jsm/lignes/${lid}`);
      toast.success("Ligne supprimée");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const removeAnalytique = async () => {
    try {
      await api.delete(`/jsm/analytiques/${aid}`);
      toast.success(`Analytique « ${a.title} » supprimé`);
      onBack(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/jsm/analytiques/${aid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const aEl = document.createElement("a");
      aEl.href = url;
      aEl.download = `JSM-${a.title}.pdf`;
      aEl.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Édition PDF impossible"); }
    finally { setPdfBusy(false); }
  };

  const downloadJustif = async (l) => {
    try {
      const r = await api.get(`/jsm/lignes/${l.id}/justificatif`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const aEl = document.createElement("a");
      aEl.href = url;
      aEl.download = l.justificatif.filename;
      aEl.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  const toggleStatut = async (l) => {
    const payload = { kind: l.kind, label: l.label, tiers: l.tiers, date: l.date,
                      statut: l.statut === "paye" ? "a_payer" : "paye" };
    if (a.tva) Object.assign(payload, { ht: l.ht, tva: l.tva, ttc: l.ttc });
    else payload.net = l.net;
    try {
      await api.patch(`/jsm/lignes/${l.id}`, payload);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const montant = (l) => (a.tva ? fmt(l.ttc) : fmt(l.net));

  return (
    <motion.div {...fadeUp} data-testid="jsm-detail">
      <button onClick={() => onBack(false)} data-testid="jsm-back"
        className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-[#A0B2CE] hover:text-gold transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Tous les analytiques
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl leading-tight" data-testid="jsm-detail-title">{a.title}</h2>
          {a.tva && <span className="inline-block mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-gold border border-gold/30 rounded-full px-2.5 py-1">TVA</span>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button onClick={exportPdf} disabled={pdfBusy} data-testid="jsm-pdf"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] text-gold border border-gold/40 hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
            {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Édition PDF
          </button>
          <ConfirmDelete onConfirm={removeAnalytique} testid="jsm-delete-analytique" />
        </div>
      </div>

      {/* Totaux */}
      <div className="grid grid-cols-3 gap-3 mb-10" data-testid="jsm-totaux">
        <div className="border border-white/10 rounded-2xl p-4 md:p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#A0B2CE] mb-1.5">Dépenses{a.tva ? " TTC" : ""}</p>
          <p className="font-serif text-xl md:text-2xl text-red-300" data-testid="jsm-total-depenses">{fmt(a.tva ? t.depenses_ttc : t.depenses)}</p>
          {a.tva && <p className="font-mono text-[10px] text-[#5A6B8C] mt-1">HT {fmt(t.depenses_ht)} · TVA {fmt(t.depenses_tva)}</p>}
        </div>
        <div className="border border-white/10 rounded-2xl p-4 md:p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#A0B2CE] mb-1.5">Recettes{a.tva ? " TTC" : ""}</p>
          <p className="font-serif text-xl md:text-2xl text-emerald-300" data-testid="jsm-total-recettes">{fmt(a.tva ? t.recettes_ttc : t.recettes)}</p>
          {a.tva && <p className="font-mono text-[10px] text-[#5A6B8C] mt-1">HT {fmt(t.recettes_ht)} · TVA {fmt(t.recettes_tva)}</p>}
        </div>
        <div className="border border-gold/30 rounded-2xl p-4 md:p-5 bg-gold/[0.04]">
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-gold mb-1.5">Solde</p>
          <p className={`font-serif text-xl md:text-2xl ${t.solde >= 0 ? "text-emerald-300" : "text-red-300"}`} data-testid="jsm-solde">{fmt(t.solde)}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-8">
        <button onClick={() => setFormKind(formKind === "depense" ? null : "depense")} data-testid="jsm-add-depense"
          className={`inline-flex items-center gap-3 px-6 py-3.5 rounded-full font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
            formKind === "depense" ? "bg-red-400/20 text-red-200 border border-red-400/40" : "border border-white/15 text-[#F3F4F6] hover:border-red-400/40 hover:text-red-300"}`}>
          <TrendingDown className="w-4 h-4" /> Ajouter une dépense
        </button>
        <button onClick={() => setFormKind(formKind === "recette" ? null : "recette")} data-testid="jsm-add-recette"
          className={`inline-flex items-center gap-3 px-6 py-3.5 rounded-full font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
            formKind === "recette" ? "bg-emerald-400/20 text-emerald-200 border border-emerald-400/40" : "border border-white/15 text-[#F3F4F6] hover:border-emerald-400/40 hover:text-emerald-300"}`}>
          <TrendingUp className="w-4 h-4" /> Ajouter une recette
        </button>
      </div>

      {editing && categories && (
        <div className="mb-10 max-w-xl">
          <LigneForm key={editing.id} kind={editing.kind} analytique={a} categories={categories} initial={editing}
            onAdded={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
        </div>
      )}

      {!editing && formKind && categories && (
        <div className="mb-10 max-w-xl">
          <LigneForm key={formKind} kind={formKind} analytique={a} categories={categories}
            onAdded={addLigne} onCancel={() => setFormKind(null)} />
        </div>
      )}

      {/* Liste des lignes */}
      <div data-testid="jsm-lignes">
        {lignes.length === 0 ? (
          <p className="text-[#A0B2CE]" data-testid="jsm-lignes-empty">Aucune ligne pour l'instant — ajoutez votre première {formKind === "recette" ? "recette" : "dépense ou recette"}.</p>
        ) : (
          <div className="divide-y divide-white/10 border-t border-b border-white/10">
            {lignes.map((l) => (
              <div key={l.id} className="py-4 flex items-center gap-4" data-testid={`jsm-ligne-${l.id}`}>
                <span className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full ${
                  l.kind === "depense" ? "bg-red-400/10 text-red-300" : "bg-emerald-400/10 text-emerald-300"}`}>
                  {l.kind === "depense" ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[#F3F4F6] truncate">{l.label}</p>
                  <p className="font-mono text-[10px] text-[#5A6B8C] uppercase tracking-[0.12em] truncate">
                    {l.date ? l.date.split("-").reverse().join("/") + " · " : ""}{l.code}{l.tiers ? ` · ${l.tiers}` : ""}
                  </p>
                </div>
                <button type="button" onClick={() => toggleStatut(l)} title="Cliquer pour changer l'état"
                  data-testid={`jsm-ligne-etat-${l.id}`}
                  className={`shrink-0 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] border transition-colors ${
                    l.statut === "paye"
                      ? "text-emerald-300 border-emerald-400/40 hover:bg-emerald-400/10"
                      : "text-amber-300 border-amber-400/40 hover:bg-amber-400/10"}`}>
                  {l.statut === "paye" ? "Payé" : "À payer"}
                </button>
                {a.tva && (
                  <p className="hidden md:block font-mono text-[11px] text-[#5A6B8C] shrink-0">
                    HT {fmt(l.ht)} · TVA {fmt(l.tva)}
                  </p>
                )}
                <p className={`font-serif text-lg shrink-0 ${l.kind === "depense" ? "text-red-300" : "text-emerald-300"}`}>
                  {l.kind === "depense" ? "−" : "+"}{montant(l)}
                </p>
                {l.justificatif && (
                  <button type="button" onClick={() => downloadJustif(l)} title={`Justificatif : ${l.justificatif.filename}`}
                    data-testid={`jsm-ligne-justif-${l.id}`}
                    className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-gold border border-gold/30 hover:bg-gold hover:text-ink transition-colors">
                    <Paperclip className="w-3.5 h-3.5" />
                  </button>
                )}
                <button type="button" onClick={() => { setEditing(l); setFormKind(null); }} data-testid={`jsm-ligne-edit-${l.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] text-[#A0B2CE] border border-white/10 hover:text-gold hover:border-gold/40 transition-colors">
                  <Pencil className="w-3 h-3" /> Modifier
                </button>
                <ConfirmDelete small onConfirm={() => removeLigne(l.id)} testid={`jsm-ligne-delete-${l.id}`} />
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ---------- Page ----------
export default function JSM() {
  const { user, checking, logout } = useAuth();
  const [openId, setOpenId] = useState(null);
  const [homeKey, setHomeKey] = useState(0);

  return (
    <div className="min-h-screen bg-ink text-[#F3F4F6] font-body">
      <div className="max-w-[900px] mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex items-center justify-between mb-14">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-[#A0B2CE] hover:text-gold transition-colors" data-testid="jsm-home-link">
            <ArrowLeft className="w-4 h-4" /> Le 30 du mois
          </Link>
          {user && user.role === "client" && (
            <button onClick={logout} data-testid="jsm-logout"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-[#A0B2CE] hover:text-gold transition-colors">
              <LogOut className="w-4 h-4" /> Se déconnecter
            </button>
          )}
        </div>

        <div className="flex items-center gap-5 mb-12">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gold/10 border border-gold/25 text-gold shrink-0">
            <Wallet className="w-7 h-7" />
          </span>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight" data-testid="jsm-title">Je suis <span className="text-gold italic">mon budget</span></h1>
        </div>

        {checking ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        ) : !user || user.role !== "client" ? (
          <JsmLogin />
        ) : openId ? (
          <JsmAnalytique aid={openId} onBack={(changed) => { setOpenId(null); if (changed) setHomeKey((k) => k + 1); }} />
        ) : (
          <JsmHome key={homeKey} onOpen={setOpenId} />
        )}
      </div>
    </div>
  );
}
